/**
 * Note: Next.js will inline environment variables at build time that are passed to the front-end whether they have a
 *       `NEXT_PUBLIC` prefix or not. This creates a problem for DevOps because they use the same build/image across
 *       environments within the same domain. We don't want environment-specific values to be used on the wrong
 *       environment, so be wary of using `NEXT_PUBLIC` or passing secrets to components. If absolutely necessary, use
 *       `noStore` to prevent inlining secrets at build time.
 *
 * @see https://nextjs.org/docs/app/building-your-application/configuring/environment-variables
 * @see https://nextjs.org/docs/app/api-reference/functions/unstable_noStore
 * @since 0.1.0
 */

/**
 * --------------------------
 * API HTTP Status Codes
 * --------------------------
 */
export const API_RESULT_CREATED = "201 Created";

export const API_RESULT_UPDATED = "204 No Content";

export const API_RESULT_SUBMITTED = "205 Reset Content";

export const API_RESULT_CLIENT_ERROR = "400 Bad Request";

export const API_RESULT_SERVER_ERROR = "500 Internal Server Error";

/**
 * -------------
 * App
 * -------------
 */
export const APP_DEFAULT_BRAND = process.env.APP_DEFAULT_BRAND || "";

export const APP_DEFAULT_THEME = process.env.APP_DEFAULT_THEME || "";

export const APP_DOMAIN = process.env.NEXT_PUBLIC_APP_DOMAIN || "";

export const APP_NAME = process.env.NEXT_PUBLIC_APP_NAME || "";

export const APP_ORIGIN = process.env.APP_ORIGIN || "";

export const APP_PROTOCOL = process.env.APP_PROTOCOL || "https";

export const AUTH_TOKEN = process.env.APP_AUTH_TOKEN;

export const APP_VERSION = process.env.npm_package_version || "";

export const DEBUG = process.env.NEXT_PUBLIC_APP_DEBUG === "true" || false;

export const DEFAULT_JOURNEY =
  process.env.NEXT_PUBLIC_APP_DEFAULT_JOURNEY || "";

export const DEVELOPMENT = "development";
export const LAB = "lab";
export const LOCAL = "local";
export const PRODUCTION = "production";
export const STAGING = "staging";
export const ENVIRONMENT = process.env.NEXT_PUBLIC_APP_ENV || PRODUCTION;

export const SECONDARY_OFFER_COOKIE_NAME = "secondary-offers-submission-id";

export const SECONDARY_OFFER_COOKIE_MAX_AGE = 86400; // 1 day is seconds

export const BENEFITS_SURVEY_COOKIE_NAME = "benefits-survey-submission-id";

export const BENEFITS_SURVEY_COOKIE_MAX_AGE = 86400; // 1 day is seconds

export const EMAIL_CALLBACK_COOKIE_NAME = "email-callback";

export const EMAIL_CALLBACK_COOKIE_MAX_AGE = 300; // 5 minutes in seconds

export const SECRET = "SECRET";

export const SITE_NAME = process.env.NEXT_PUBLIC_APP_SITE_NAME || "";

export const USER_COOKIE_MAX_AGE = 7776000; // 90 days in seconds

export const USER_COOKIE_NAME = "user";

export const AUTH_SESSION_COOKIE_NAME = "auth-session";

export const VISIT_COOKIE_MAX_AGE = 86400; // 24 hours in seconds

export const VISIT_COOKIE_NAME = "visit";

export const RETURN_VISIT_COOKIE_NAME = "returningVisitor";

export const RETURN_VISIT_INGRESS_URL_COOKIE_NAME =
  "returningVisitorOriginalIngressUrl";

export const EXTERNAL_REDIRECT_SITES = ["stripes.com"];

export const EXTERNAL_RETURN_VISIT_FLAG = "referredBy";

export const CYPRESS_REDIRECT_FLAG = "cypress_redirect_flag";

export const CYPRESS_REDIRECT_RETURN_FLAG = "cypress_redirect_return_flag";

/**
 * -------------
 * Attribution
 * -------------
 */

// Attribution parameter lookup set for O(1) performance
export const ATTRIBUTION_PARAMS = new Set([
  // ad_group
  "ad_group",
  "adgroup",
  "adset_name",
  "ovadgrpid",
  "ysmadgrpid",
  // ad_type
  "ad_type",
  "adtype",
  // affiliate
  "affiliate",
  "aff",
  "affid",
  // app
  "aid",
  // bid_match_type
  "bidmatchtype",
  "bid_match_type",
  "bmt",
  // brand
  "brand",
  "brand_name",
  // campaign
  "campaign",
  "campaign_name",
  "utm_campaign",
  "ovcampgid",
  "ysmcampgid",
  "cn",
  // campaign_identifier
  "campaign_identifier",
  "campaignidentifier",
  "campaignid",
  "campaign_id",
  "cid",
  "utm_campaign_id",
  // click_id
  "click_id",
  "clickid",
  "dclid",
  "fbclid",
  "gclid",
  "gclsrc",
  "msclkid",
  "zanpid",
  "ttclid",
  // content
  "content",
  "ad_name",
  "utm_content",
  "cc",
  // content_identifier
  "content_identifier",
  "contentidentifier",
  "contentid",
  "content_id",
  "cntid",
  "utm_content_id",
  // creative
  "creative",
  "adid",
  "ovadid",
  // device_type
  "device_type",
  "devicetype",
  "device",
  // experiment
  "experiment",
  "aceid",
  // keyword
  "keyword",
  "kw",
  "utm_term",
  "ovkey",
  "ysmkey",
  // match_type
  "match_type",
  "matchtype",
  "match",
  "ovmtc",
  "ysmmtc",
  // medium
  "medium",
  "utm_medium",
  "cm",
  // medium_identifier
  "medium_identifier",
  "mediumidentifier",
  "mediumid",
  "medium_id",
  "mid",
  "utm_medium_id",
  // network
  "network",
  "anid",
  // placement
  "placement",
  // position
  "position",
  "adposition",
  "ad_position",
  // search_term
  "search_term",
  "searchterm",
  "q",
  "querystring",
  "ovraw",
  "ysmraw",
  // source
  "source",
  "utm_source",
  "cs",
  // subsource
  "subsource",
  "subid",
  "sid",
  "effi_id",
  "customid",
  "afftrack",
  "pubref",
  "argsite",
  "fobs",
  "epi",
  "ws",
  "u1",
  // target
  "target",
  // tiktok_pixel_cookie (note: original was truncated, including common tiktok params)
  "ttclid",
  "ttp",
]);

// Click ID and TikTok pixel params that should be excluded from comparison
export const EXCLUDED_FROM_COMPARISON = new Set([
  "click_id",
  "clickid",
  "dclid",
  "fbclid",
  "gclid",
  "gclsrc",
  "msclkid",
  "zanpid",
  "ttclid",
  "ttp",
]);

export const E2E_UTM_SOURCE_FLAG = "end_to_end_utm_source";
export const E2E_UTM_SOURCE_VALUE = "cypress";
export const E2E_PPC_JOURNEY_VARIATION = "ppc_cypress_test";

/**
 * Prevents LD journey variations from altering the url.
 * Preserves url for Google Search.
 */
export const JOURNEY_VARIATION_PARAM_NAME = "variation";
export const PPC_PATH_NAME = "/ppc";
export const PPC_JOURNEY_NAME = "ppc";
/**
 * -------------
 * ButterCMS
 * -------------
 */
export const BUTTER_CMS = "ButterCMS";

export const BUTTER_CMS_API_KEY = process.env.BUTTER_CMS_API_KEY || "";

export const BUTTER_CMS_DEFAULT_DOMAIN = "billdoctor.org";

export const BUTTER_CMS_DOMAIN_PREFIX =
  process.env.BUTTER_CMS_DOMAIN_PREFIX || "";

export const BUTTER_CMS_POSTS_CATEGORY =
  process.env.BUTTER_CMS_POSTS_CATEGORY || "";

export const VALID_CATEGORIES = [
  "budgeting",
  "debt-management",
  "life-and-career",
  "savings",
  "events",
  "news-and-events",
  "videos",
] as const;

export const BUTTER_CMS_POSTS_LIMIT = 12;

export const ARTICLE_DISPLAY_SMALL = 3;
export const ARTICLE_DISPLAY_MED = 6;
export const ARTICLE_DISPLAY_LARGE = 12;

export const FIN_ED_HERO_ARTICLES = [
  "financial-freedom-for-veterans-vda",
  "why-veterans-are-drowning-in-debt-vda",
  "why-budgeting-feels-so-hard-after-the-military-and-what-actually-works-vda",
];

export const RESOURCE_SEARCH_MAX_QUERY_LENGTH = 60;

/**
 * -------------
 * Braze
 * -------------
 */
export const BRAZE = "Braze";

export const BRAZE_API_KEY = process.env.NEXT_PUBLIC_BRAZE_API_KEY || "";

export const BRAZE_SDK_ENDPOINT =
  process.env.NEXT_PUBLIC_BRAZE_SDK_ENDPOINT || "";

/**
 * -------------
 * Maze
 * -------------
 */

export const MAZE_APPROVED_DOMAINS_AND_ENVIRONMENTS: Record<string, string[]> =
  {
    savewise: [PRODUCTION],
    "veteran-debt-assistance": [STAGING, LOCAL],
  };

/**
 * -------------
 * Contentful
 * -------------
 */
export const CONTENTFUL_SPACE_ID = process.env.CONTENTFUL_SPACE_ID || "";
export const CONTENTFUL_ACCESS_TOKEN =
  process.env.CONTENTFUL_ACCESS_TOKEN || "";
export const CONTENTFUL_PREVIEW_ACCESS_TOKEN =
  process.env.CONTENTFUL_PREVIEW_ACCESS_TOKEN || "";

/**
 * -------------
 * CookieBot
 * -------------
 */
export const COOKIEBOT = "Cookiebot";

export const COOKIEBOT_ID = process.env.NEXT_PUBLIC_COOKIEBOT_ID || "";

/**
 * -------------
 * Datadog
 * -------------
 */
export const DATA_DOG_APP_ID = process.env.NEXT_PUBLIC_DATA_DOG_APP_ID || "";

export const DATA_DOG_CLIENT_TOKEN =
  process.env.NEXT_PUBLIC_DATA_DOG_CLIENT_TOKEN || "";

export const DATA_DOG_SERVICE_NAME =
  process.env.NEXT_PUBLIC_DATA_DOG_SERVICE_NAME || "";

/**
 * -------------
 * Events
 * -------------
 */
/**
 *
 * @description Used by Google Tag Manager (GTM) to track user interactions
 */
export const USER_INTERACTION_DATA_ATTRIBUTE = "data-track-interaction";
/**
 *
 * @description Used by Google Tag Manager (GTM) to track user interactions
 */
export const USER_INTERACTION_DATA_ATTRIBUTE_SPREAD_ME = {
  [USER_INTERACTION_DATA_ATTRIBUTE]: "default",
};

/**
 *
 * @description Used by Google Tag Manager (GTM) to track user interactions
 */
export const setUserInteractionDataAttribute = (value = "default") => {
  return {
    [USER_INTERACTION_DATA_ATTRIBUTE]: value,
  };
};
export const EVENT_BENEFITS_SURVEY_OUTCOME_CALCULATED =
  "benefitsSurveyOutcomeCalculated";

export const EVENT_COOKIE_CONSENT_VIEWED = "cookieConsentViewed";

export const EVENT_COOKIE_CONSENT_SUBMITTED = "cookieConsentSubmitted";

export const EVENT_ERROR = "error";

export const EVENT_DATA_LAYER_INITIALIZED = "dataLayerInitialized";

export const EVENT_DATA_LAYER_READY = "dataLayerReady";

export const EVENT_USER_COOKIE_READY = "userCookieReady";

export const EVENT_VISIT_COOKIE_READY = "visitCookieReady";

export const EVENT_CTA_CLICKED = "CTAClicked";

export const EVENT_ELIGIBLE_BENEFIT_CLICKED = "eligibleBenefitClicked";

export const EVENT_JOURNEY_STARTED = "journeyStarted";

export const EVENT_JOURNEY_UPDATED = "journeyUpdated";

export const EVENT_LANDING_PAGE_VIEWED = "landingPageView";

export const EVENT_ATTRIBUTION_PARAMS_UPDATED = "attributionParamsUpdated";

export const EVENT_CLICK_ID_UPDATED = "clickIdUpdated";

export const EVENT_STEP_SUBMITTED = "stepSubmitted";

export const EVENT_STEP_VIEWED = "leadFormStepView";

export const EVENT_TRIGGER_NON_ESSENTIAL_SCRIPTS = "triggerNonEssentialScripts";

export const EVENT_LEAD_FORM_SUBMITTED = "leadFormSubmitted";

export const EVENT_NPS_5 = "NPS5";

export const EVENT_PAGE_NOT_FOUND = "pageNotFound";

export const EVENT_PAGE_VIEWED = "pageView";

export const EVENT_REDIRECT = "redirect";

export const EVENT_SECONDARY_OFFER_CLICKED = "secondaryOfferClicked";

export const EVENT_SECONDARY_OFFERS_FORM_SUBMITTED =
  "secondaryOffersFormSubmitted";

export const EVENT_SECONDARY_OFFERS_STEP_SUBMITTED =
  "secondaryOffersFormStepSubmitted";

export const EVENT_SURVEY_OUTCOME_CALCULATED = "surveyOutcomeCalculated";

export const EVENT_THANK_YOU_PAGE_VIEWED = "thankYouPageView";

export const EVENT_BEYOND_DOWN_THANK_YOU_PAGE_VIEWED =
  "beyondDownThankYouPageViewed";

export const EVENT_ADR_QUOTE_THANK_YOU_PAGE_VIEWED =
  "adrQuoteThankYouPageViewed";

/**
 * -------------
 * Facebook
 * -------------
 */
export const FACEBOOK = "Facebook";

export const FACEBOOK_APP_ID = process.env.NEXT_PUBLIC_FACEBOOK_APP_ID || "";

/**
 * -------------
 * Front-End API
 * -------------
 */
export const FRONT_END_API = "Front-End API";

export const FRONT_END_API_AUTH_TOKEN =
  process.env.FRONT_END_API_AUTH_TOKEN || "";

export const FRONT_END_API_BASE_URL = process.env.FRONT_END_API_BASE_URL || "";

/**
 * -------------
 * Google
 * -------------
 */
export const GOOGLE_TAG_MANAGER = "GTM";

export const GOOGLE_TAG_MANAGER_ID = process.env.GOOGLE_TAG_MANAGER_ID || "";

export const GOOGLE_TAG_MANAGER_AUTH =
  process.env.GOOGLE_TAG_MANAGER_AUTH || "";

export const GOOGLE_TAG_MANAGER_PREVIEW =
  process.env.GOOGLE_TAG_MANAGER_PREVIEW || "";

/**
 * -------------
 * Heap
 * -------------
 */
export const HEAP = "Heap";

export const HEAP_APP_ID = process.env.HEAP_APP_ID || "";

/**
 * -------------
 * Hotjar
 * -------------
 */
export const HOTJAR = "Hotjar";

export const HOTJAR_APP_ID = process.env.NEXT_PUBLIC_HOTJAR_APP_ID || "";

/**
 * -------------
 * Impact
 * -------------
 */
export const IMPACT_APP_ID = process.env.NEXT_PUBLIC_IMPACT_APP_ID || "";

/**
 * -------------
 * Land
 * -------------
 */
export const LAND = "Land";

export const LAND_DOMAIN_NAME = process.env.NEXT_PUBLIC_LAND_DOMAIN_NAME || "";

export const LAND_SITE_NAME = process.env.NEXT_PUBLIC_LAND_SITE_NAME || "";

/**
 * -------------
 * LaunchDarkly
 * -------------
 */
export const LAUNCHDARKLY = "LaunchDarkly";

export const LAUNCHDARKLY_CLIENT_SDK_KEY = "";

export const LAUNCHDARKLY_DOMAIN_PREFIX =
  process.env.LAUNCHDARKLY_DOMAIN_PREFIX || "";

export const LAUNCHDARKLY_SERVER_SDK_KEY =
  process.env.LAUNCHDARKLY_SERVER_SDK_KEY || "";

export const LAUNCHDARKLY_REDIRECT_QUERY_PARAM = "redirect";

/**
 * -------------
 * Lead Form
 * -------------
 */
export const DEFAULT_HERO_STEP = "debt-amount";

export const DEFAULT_LEAD_FORM_STEPS = [
  { name: "payment-situation", label: "" },
  { name: "student-loan-debt", label: "" },
  { name: "zip-code", label: "" },
  { name: "street-address", label: "" },
  { name: "city", label: "" },
  { name: "state", label: "" },
  { name: "dob", label: "" },
  { name: "name", label: "" },
  { name: "income-amount", label: "" },
  { name: "phone-number", label: "" },
  { name: "email", label: "" },
];

export const LEAD_FORM_CREATE = "create";

export const LEAD_FORM_FLOW = "lead-form";

export const LEAD_FORM_LOCAL_STORAGE_KEY = "lead-form";

export const LEAD_FORM_SUBMIT = "submit";

export const LEAD_FORM_UPDATE = "update";

const _providers = {
  ACCREDITED: "accredited",
  CAMBRIDGE: "cambridge",
  CAMBRIDGE_REDIRECT: "cambridge-redirect",
  CREDIBLE: "credible",
  FIONA: "fiona",
  MONEYLION_DEBT_SETTLEMENT: "moneylion-debt-settlement",
  DEBT_DOT_COM: "debt-dot-com",
  RATE_CHOPPER: "rate-chopper",
  DEFAULT: "",
};

_providers.DEFAULT = _providers.CREDIBLE;

export const PROVIDERS = Object.freeze(_providers);

export const PROVIDER_MAP = {
  beyond_finance: PROVIDERS.ACCREDITED,
  cambridge: PROVIDERS.CAMBRIDGE_REDIRECT,
  credible: PROVIDERS.CREDIBLE,
  even_finance: PROVIDERS.FIONA,
  moneylion: PROVIDERS.MONEYLION_DEBT_SETTLEMENT,
};

export const PROVIDER_LIST = Object.freeze(Object.values(PROVIDER_MAP));

export const NO_OFFERS_PRESENT = "free-resources";

export const CAMBRIDGE_CTA_HREF = "https://www.cambridge-credit.org/cpc/offer";

/**
 * -------------
 * Domain Stats
 * -------------
 */

export const GENERIC_PERSONA = "individuals";
const VETERANS_PERSONA = "veterans";
const NURSES_PERSONA = "nurses";
const TEACHERS_PERSONA = "teachers";

type PERSONAS =
  | typeof GENERIC_PERSONA
  | typeof VETERANS_PERSONA
  | typeof NURSES_PERSONA
  | typeof TEACHERS_PERSONA;

const DOMAIN_PERSONAS = {
  americandebtassistance: GENERIC_PERSONA,
  americanloanassistance: GENERIC_PERSONA,
  billdoctor: GENERIC_PERSONA,
  divorcedebtsolutions: GENERIC_PERSONA,
  thedebtfreeveteran: VETERANS_PERSONA,
  financiallyfreenurse: NURSES_PERSONA,
  thedebtfreefirstresponder: GENERIC_PERSONA,
  thedebtfreeteacher: TEACHERS_PERSONA,
  veterandebtassistance: VETERANS_PERSONA,
  veteranfamilydebtassistance: GENERIC_PERSONA,
  uniondebtassistance: GENERIC_PERSONA,
  savewise: GENERIC_PERSONA,
} as const;

export const DOMAIN_PERSONA_MAP = Object.freeze(DOMAIN_PERSONAS);

export const GET_DOMAIN_PERSONA = (
  domain: keyof typeof DOMAIN_PERSONA_MAP = APP_DOMAIN as keyof typeof DOMAIN_PERSONA_MAP,
): PERSONAS => {
  return DOMAIN_PERSONA_MAP[domain] || GENERIC_PERSONA;
};

/**
 * -------------
 * Neverbounce
 * -------------
 */

export const EVENT_NEVERBOUNCE_SUCCESS = "neverbounce_validation_success";

export const EVENT_NEVERBOUNCE_FAILURE = "neverbounce_validation_failure";

export const EVENT_NEVERBOUNCE_ERROR = "neverbounce_validation_error";

export const NEVERBOUNCE_PRIVATE_API_KEY =
  process.env.NEVERBOUNCE_PRIVATE_API_KEY || "";

export const NEVERBOUNCE_PUBLIC_API_KEY =
  process.env.NEVERBOUNCE_PUBLIC_API_KEY || "";

export const AUTH_FORM_CREATE_ACCOUNT_TEXT = "Create an Account";

export const AUTH_FORM_LOG_IN_TEXT = "Log In";

export const AUTH_FORM_GENERIC_ERROR_MESSAGE =
  "Something went wrong, please try again.";

export const AUTH_FORM_EMAIL_TAKEN_MESSAGE = "The email has already been taken";

export const AUTH_LOGGED_OUT_MESSAGE = "You have been logged out";

export const LOGGED_OUT_QUERY_PARAM = "loggedOut";

export const AUTH_CONFIRM_SUCCESS_PATH = "/dashboard";

export const PASSWORD_STRENGTH_LABEL = "Password Strength: ";

export const PASSWORD_STRENGTH_NONE = "None";

export const PASSWORD_STRENGTH_WEAK = "Weak";

export const PASSWORD_STRENGTH_AVERAGE = "Average";

export const PASSWORD_STRENGTH_GOOD = "Good";

/**
 * -------------
 * Benefits Survey
 * -------------
 */
export const DEFAULT_BENEFITS_SURVEY_STEPS = [
  { name: "service", label: "" },
  { name: "discharge", label: "" },
  { name: "illness-and-injury", label: "" },
  { name: "september-11", label: "" },
  { name: "education-and-duty", label: "" },
  { name: "service-branch", label: "" },
  { name: "iadt", label: "" },
  { name: "disability", label: "" },
  { name: "va-service", label: "" },
  { name: "net-worth", label: "" },
  { name: "care", label: "" },
  { name: "wartime-vet", label: "" },
  { name: "housing", label: "" },
  { name: "sgli", label: "" },
  { name: "disability-rating", label: "" },
  { name: "age", label: "" },
];

export const BENEFITS_SURVEY_CREATE = "create";

export const BENEFITS_SURVEY_SUBMIT = "submit";

export const BENEFITS_SURVEY_UPDATE = "update";

export const SURVEY_CREATE = "create";
export const SURVEY_SUBMIT = "submit";
export const SURVEY_UPDATE = "update";

/**
 * -------------
 * Redis
 * -------------
 */
export const REDIS_URL = process.env.REDIS_URL || "redis://localhost:6379/0";

/**
 * -------------
 * Secondary Offers Form
 * -------------
 */
export const DEFAULT_SECONDARY_OFFERS_STEPS = [
  { name: "housing", label: "" },
  { name: "employment", label: "" },
  { name: "payment", label: "" },
  { name: "education", label: "" },
  { name: "amount", label: "" },
  { name: "ssn", label: "" },
];

export const SECONDARY_OFFERS_FORM_FLOW = "secondary-offers-form";

export const SECONDARY_OFFERS_FORM_CREATE = "create";

export const SECONDARY_OFFERS_FORM_SUBMIT = "submit";

export const SECONDARY_OFFERS_FORM_UPDATE = "update";

export const SUBMISSION_TYPE_EVEN_FINANCE_PREQUAL = "even_finance_prequal";

/**
 * -------------
 * TikTok
 * -------------
 */

export const TIKTOK = "Tiktok";

export const TIKTOK_APP_ID = process.env.NEXT_PUBLIC_TIKTOK_APP_ID || "";

/**
 * -------------
 * Twilio
 * -------------
 */

export const TWILIO_ACCOUNT_SID = process.env.TWILIO_ACCOUNT_SID || "";

export const TWILIO_API_KEY_SID = process.env.TWILIO_API_KEY_SID || "";

export const TWILIO_API_KEY_SECRET = process.env.TWILIO_API_KEY_SECRET || "";

export const TWILIO_TIMEOUT = 3000; // 3 seconds

/**
 * -------------
 * TrustPilot
 * -------------
 */

export const TRUST_PILOT_BUSINESS_UNIT_ID =
  process.env.NEXT_PUBLIC_TRUST_PILOT_BUSINESS_UNIT_ID || "";
export const TRUST_PILOT_URL = process.env.NEXT_PUBLIC_TRUST_PILOT_URL || "";

/**
 * -------------
 * URL Parameters
 * -------------
 */

export const URL_PARAM_GOOGLE_LOCATION_ID = "loc_physical_ms";

/**
 * -------------
 * Yup
 * -------------
 */

export const PHONE_NUMBER_ERROR_MESSAGE = "Please enter your phone number";
export const ONE_LINE_ADDRESS_ERROR_MESSAGE = "Please enter your address";
export const ZIP_CODE_ERROR_MESSAGE = "Please enter your 5-digit zip code";
export const EMAIL_REQUIRED_ERROR_MESSAGE = "Email is required";
export const INVALID_EMAIL_ERROR_MESSAGE = "Invalid email";
export const PASSWORD_REQUIRED_ERROR_MESSAGE = "Password is required";
export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MIN_LENGTH_ERROR_MESSAGE =
  "Must be at least 8 characters in length";
